// ===================================================================
// KAIXO - ForwardModal
// ===================================================================
// Modal para reenviar un mensaje a N destinatarios (conversaciones
// existentes, grupos, o usuarios con los que no hay chat todavía).
// Hasta ahora vivía inline en ChatWindow; se extrae para reducir el
// monolito y permitir reutilización.
// ===================================================================

import React from 'react';
import t from '../../i18n/useTranslation';
import useModalClose from '../../hooks/useModalClose';

export default function ForwardModal({
    conversations,
    groups,
    allUsers,
    targets,            // array de IDs seleccionados
    onToggleTarget,     // (id) => void
    onSend,             // () => void
    onClose             // () => void
}) {
    useModalClose(onClose);
    const otherUsers = allUsers.filter(
        u => !conversations.some(c => c.otherUser?.id === u._id)
    );
    const isSelected = (id) => targets.includes(id);

    return (
        <div className="delete-modal-overlay" onClick={onClose}>
            <div className="forward-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="delete-modal-title">{t.chat.forwardMsg}</h3>

                <div className="forward-list">
                    {conversations.length > 0 && (
                        <>
                            <div className="forward-section-title">{t.chat.conversations}</div>
                            {conversations.map(conv => (
                                <div
                                    key={conv.id}
                                    className={`forward-item ${isSelected(conv.id) ? 'selected' : ''}`}
                                    onClick={() => onToggleTarget(conv.id)}
                                >
                                    <div className="forward-item-avatar">
                                        {conv.otherUser.avatar
                                            ? <img src={conv.otherUser.avatar} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                            : conv.otherUser.name.charAt(0)}
                                    </div>
                                    <div className="forward-item-name">{conv.otherUser.name}</div>
                                    <div className={`forward-checkbox ${isSelected(conv.id) ? 'checked' : ''}`}>
                                        {isSelected(conv.id) && '✓'}
                                    </div>
                                </div>
                            ))}
                        </>
                    )}

                    {groups.length > 0 && (
                        <>
                            <div className="forward-section-title">{t.chat.groups}</div>
                            {groups.map(group => {
                                // Backend devuelve grupos con `_id` (no `id`).
                                // Antes usábamos `group.id` aquí: undefined para todos
                                // los grupos → todos compartían key, isSelected aplicaba
                                // a undefined, y al marcar uno se "marcaban" todos.
                                const gid = group._id || group.id;
                                return (
                                    <div
                                        key={gid}
                                        className={`forward-item ${isSelected(gid) ? 'selected' : ''}`}
                                        onClick={() => onToggleTarget(gid)}
                                    >
                                        <div className="forward-item-avatar">
                                            {group.avatar
                                                ? <img src={group.avatar} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                                : '👥'}
                                        </div>
                                        <div className="forward-item-name">{group.name}</div>
                                        <div className={`forward-checkbox ${isSelected(gid) ? 'checked' : ''}`}>
                                            {isSelected(gid) && '✓'}
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}

                    {otherUsers.length > 0 && (
                        <>
                            <div className="forward-section-title">Otros usuarios</div>
                            {otherUsers.map(user => (
                                <div
                                    key={user._id}
                                    className={`forward-item ${isSelected(user._id) ? 'selected' : ''}`}
                                    onClick={() => onToggleTarget(user._id)}
                                >
                                    <div className="forward-item-avatar">
                                        {user.avatar
                                            ? <img src={user.avatar} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                            : user.name.charAt(0)}
                                    </div>
                                    <div className="forward-item-name">
                                        {user.name} <span style={{ fontSize: '11px', color: '#999' }}>({user.language})</span>
                                    </div>
                                    <div className={`forward-checkbox ${isSelected(user._id) ? 'checked' : ''}`}>
                                        {isSelected(user._id) && '✓'}
                                    </div>
                                </div>
                            ))}
                        </>
                    )}

                    {conversations.length === 0 && groups.length === 0 && allUsers.length === 0 && (
                        <div className="forward-empty">{t.chat.loading2}</div>
                    )}
                </div>

                <button
                    className="delete-modal-btn"
                    style={{
                        background: targets.length > 0 ? '#F5A000' : '#e0e0e0',
                        color: targets.length > 0 ? 'white' : '#999'
                    }}
                    onClick={onSend}
                    disabled={targets.length === 0}
                >
                    {t.chat.forward} {targets.length > 0 ? `(${targets.length})` : ''}
                </button>
                <button
                    className="delete-modal-btn-cancel delete-modal-btn"
                    onClick={onClose}
                >
                    {t.chat.cancel}
                </button>
            </div>
        </div>
    );
}
